// ⚡️ CHAT HEADER: Header section with user avatar and info
// Extracted from ChatPanel.js for better organization and reusability

import React, { useMemo } from 'react';

// WhatsApp-style last seen formatter
const formatLastSeen = (lastSeenTimestamp) => {
  if (!lastSeenTimestamp) return 'Offline';
  
  const now = Date.now();
  const diff = now - lastSeenTimestamp;
  
  // Less than 1 minute
  if (diff < 60000) {
    return 'last seen just now';
  }
  
  // Less than 1 hour
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `last seen ${minutes} min ago`;
  }
  
  // Less than 24 hours
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `last seen ${hours} hr ago`;
  }
  
  // 24 hours or more - show date and time
  const date = new Date(lastSeenTimestamp);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
  
  return `last seen ${month} ${day}, ${year} • ${time}`;
};

const ChatHeader = ({
  conversation,
  otherPartyName,
  otherPartyAvatar,
  otherPartyOnline,
  otherPartyLastSeen,
  isDarkMode,
  onSelectConversation,
  dispatch,
  buildHeaderAvatar
}) => {
  return (
    <div className="relative">
      
      <div className={`flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 mb-2 mt-2 sm:mt-4 shadow-lg rounded-2xl sm:rounded-3xl mx-2 sm:mx-4 border ${
        isDarkMode ? 'bg-[#2a2a2a] border-gray-700/50' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Back Arrow - Only show on mobile/tablet */}
          <button 
            onClick={() => {
              onSelectConversation(null);
              // No navigation - stay in popup
            }}
            className={`xl:hidden p-1.5 sm:p-2 rounded-full transition-colors mr-1 sm:mr-2 ${
              isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'
            }`}
            aria-label="Back to conversations"
          >
            <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div className="relative">
            {/* User Avatar */}
            <img 
              src={otherPartyAvatar || conversation?.avatar} 
              alt={otherPartyName || conversation?.name} 
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
            />
            {/* Online Status Indicator */}
            <span className={`absolute bottom-0.5 sm:bottom-1 right-0.5 sm:right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full border-2 border-white ${otherPartyOnline ? 'bg-green-500' : 'bg-gray-400'}`}></span>
          </div>
          
          <div className="flex-1 min-w-0">
            {/* User Name */}
            <div className={`font-semibold text-sm sm:text-base truncate ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {otherPartyName ? `${otherPartyName} , YTproud Agent` : (conversation?.name ? `${conversation?.name} , YTproud Agent` : 'Unknown User , YTproud Agent')}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Online Status Text */}
              <div className={`text-xs ${otherPartyOnline ? 'text-green-400' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                {otherPartyOnline ? 'Online' : formatLastSeen(otherPartyLastSeen)}
              </div>
              
              {/* Product Button */}
              {!conversation?.isAdmin && (
                <button 
                  onClick={() => dispatch({ type: 'SET_SHOW_PRODUCT_MODAL', payload: true })}
                  className="bg-red-500 hover:bg-red-600 rounded-full px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold text-white shadow"
                >
                  Product
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatHeader;
